import { type CameraControls, CameraControlsImpl } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface ArticleToolsProps {
    cameraControlsRef: RefObject<CameraControls | null>;
    isMainCamera: boolean;
}

const TIMEOUT_DELAY_MS = 300;

export const useCameraControlsEvents = ({ cameraControlsRef, isMainCamera }: ArticleToolsProps) => {
    const { handleLockCameraControls, setFocusOnBuilding } = usePlacementStore(
        useShallow((state) => ({
            handleLockCameraControls: state.handleLockCameraControls,
            setFocusOnBuilding: state.setFocusOnBuilding
        }))
    );

    const isMouseWheelPressedRef = useRef<boolean>(false);
    const isShiftPressedRef = useRef<boolean>(false);

    const wheelClickCountRef = useRef<number>(0);
    const wheelClickTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const gl = useThree((state) => state.gl);

    const updateCameraRotationSettings = useCallback(() => {
        if (cameraControlsRef.current) {
            const rotationEnabled = isMouseWheelPressedRef.current && isShiftPressedRef.current;
            const action = rotationEnabled ? CameraControlsImpl.ACTION.ROTATE : CameraControlsImpl.ACTION.TRUCK;
            if (action !== cameraControlsRef.current.mouseButtons.middle) {
                cameraControlsRef.current.mouseButtons.middle = action;
            }
            if (isMainCamera && rotationEnabled) {
                handleLockCameraControls(false);
            }
        }
    }, [cameraControlsRef, handleLockCameraControls, isMainCamera]);

    const handleOnPointerDown = useCallback(
        (event: PointerEvent) => {
            if (event.target !== gl.domElement) {
                return;
            }

            if (event.button === 1) {
                isMouseWheelPressedRef.current = true;
                isShiftPressedRef.current = event.shiftKey;
                updateCameraRotationSettings();

                if (!isMainCamera) {
                    return;
                }

                wheelClickCountRef.current++;

                if (wheelClickCountRef.current === 2) {
                    setFocusOnBuilding(cameraControlsRef, gl.domElement);
                    clearTimeout(wheelClickTimerRef.current);
                    wheelClickCountRef.current = 0;
                } else {
                    wheelClickTimerRef.current = setTimeout(() => {
                        wheelClickCountRef.current = 0;
                    }, TIMEOUT_DELAY_MS);
                }
            }
        },
        [updateCameraRotationSettings, isMainCamera, setFocusOnBuilding, cameraControlsRef, gl.domElement]
    );

    const handleOnPointerUp = useCallback(
        (event: PointerEvent) => {
            if (event.button === 1) {
                isMouseWheelPressedRef.current = false;
                updateCameraRotationSettings();
            }
        },
        [updateCameraRotationSettings]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Shift') {
                isShiftPressedRef.current = true;
                updateCameraRotationSettings();
            }
        },
        [updateCameraRotationSettings]
    );

    const handleKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Shift') {
                isShiftPressedRef.current = false;
                updateCameraRotationSettings();
            }
        },
        [updateCameraRotationSettings]
    );

    useEffect(() => {
        window.addEventListener('pointerdown', handleOnPointerDown, { capture: true });
        window.addEventListener('pointerup', handleOnPointerUp, { capture: true });
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('pointerdown', handleOnPointerDown, { capture: true });
            window.removeEventListener('pointerup', handleOnPointerUp, { capture: true });
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [handleKeyDown, handleKeyUp, handleOnPointerDown, handleOnPointerUp]);
};
